/**
 * Product-card color swatches.
 *
 * Clicking a chip in the hover color grid selects that color instead of
 * navigating: the chip becomes `.is-active`, the card's quick-add button
 * gets the chip's variant URL as `data-product-url` (so the quick-add modal
 * and its add-to-cart open on the selected color's variant), and the card's
 * media/title links are repointed at the same URL so opening the product
 * lands on the selected colour. The chip's href stays as the no-JS fallback.
 *
 * Colour-sibling chips (from the custom.color_variants metafield) work the
 * same way: their href is the sibling product's PDP, so the quick-add modal
 * opens that product and add-to-cart adds it.
 *
 * Chips also carry data-card-image(-*) attributes (see product-card.liquid /
 * color-variant-chip.liquid) — selecting a chip swaps the card's primary
 * image to that colour's image. Sibling chips additionally carry
 * data-card-secondary-image (always present, possibly empty) to swap or hide
 * the hover image; variant chips omit it, leaving the hover image alone.
 * Because the chip grid is hover-only, selecting a chip also pins the primary
 * image for the remainder of that hover, so the chosen colour is actually
 * visible instead of the hover image.
 *
 * Chips further carry product/variant identity (data-variant-id, prices,
 * availability; sibling chips also data-product-id/-handle/-title) —
 * selecting a chip retargets the card's wishlist heart at that variant and,
 * for sibling chips, repoints the compare checkbox at the sibling product.
 */
// The whole file is an IIFE: this script tag is emitted once per
// card-rendering section, and top-level consts would throw
// "already declared" on the second execution.
(() => {
// Swap-image srcsets are derived from the chip's width=540 data URL instead
// of being embedded per chip in the HTML (they cost ~70KB of markup per page).
// Must produce the exact srcset Liquid used to render: 200w–960w, ', '-joined.
const SWAP_SRCSET_WIDTHS = [200, 360, 540, 720, 960];
const swapSrcsetFromUrl = (url) =>
  url && url.includes('width=540')
    ? SWAP_SRCSET_WIDTHS.map((w) => `${url.replace('width=540', `width=${w}`)} ${w}w`).join(', ')
    : '';

document.addEventListener('click', (event) => {
  const chip = event.target.closest('.c-product-card__color-chip');
  if (!chip) return;

  event.preventDefault();

  const card = chip.closest('.c-product-card');
  if (!card) return;

  card
    .querySelectorAll('.c-product-card__color-chip.is-active')
    .forEach((activeChip) => activeChip.classList.remove('is-active'));
  chip.classList.add('is-active');

  const href = chip.getAttribute('href');
  const cartButton = card.querySelector('.c-product-card__cart-button');
  if (href && cartButton) {
    cartButton.setAttribute('data-product-url', href);
  }

  // Opening the card must land on the selected colour too — repoint the
  // media and title links at the chip's URL (a ?variant= URL for
  // option-value chips, the sibling product's PDP for colour-sibling chips).
  if (href) {
    card
      .querySelectorAll('.c-product-card__media-link, .c-product-card__title-link')
      .forEach((link) => link.setAttribute('href', href));
  }

  const primaryImage = card.querySelector('.c-product-card__image--primary');
  if (primaryImage && chip.dataset.cardImage) {
    primaryImage.src = chip.dataset.cardImage;
    primaryImage.srcset = swapSrcsetFromUrl(chip.dataset.cardImage);
    if (chip.dataset.cardImageAlt !== undefined) {
      primaryImage.alt = chip.dataset.cardImageAlt;
    }
  }

  const secondaryImage = card.querySelector('.c-product-card__image--secondary');
  if (secondaryImage && 'cardSecondaryImage' in chip.dataset) {
    if (chip.dataset.cardSecondaryImage) {
      secondaryImage.src = chip.dataset.cardSecondaryImage;
      secondaryImage.srcset = swapSrcsetFromUrl(chip.dataset.cardSecondaryImage);
      card.classList.add('c-product-card--has-secondary-image');
    } else {
      card.classList.remove('c-product-card--has-secondary-image');
    }
  }

  // The chip grid only appears on hover, so the card is showing its hover
  // (second) image at the moment of the click — the shopper would never see
  // the colour they just picked. Hold the primary image for the rest of this
  // hover and release on mouseleave, so hovering the card again swaps to the
  // second image as normal. See component-product-card.css.
  const suppressClass = 'c-product-card--suppress-hover-image';
  if (!card.classList.contains(suppressClass)) {
    card.classList.add(suppressClass);
    card.addEventListener('mouseleave', () => card.classList.remove(suppressClass), { once: true });
  }

  // Wishlist: retarget the heart at the selected colour. Wishlist entries are
  // keyed by variant id, so hearting after a swatch change saves that colour
  // (sibling product or same-product variant). Chips without variant data
  // (e.g. no matching variant) leave the heart on the card's own product.
  const wishlistButton = card.querySelector('[data-wishlist-toggle]');
  if (wishlistButton && chip.dataset.variantId) {
    [
      'productId',
      'productHandle',
      'productTitle',
      'variantId',
      'variantTitle',
      'productPrice',
      'comparePrice',
      'available',
    ].forEach((key) => {
      if (chip.dataset[key] !== undefined) wishlistButton.dataset[key] = chip.dataset[key];
    });
    if (href) wishlistButton.dataset.productUrl = href;
    if (chip.dataset.cardImage) wishlistButton.dataset.productImage = chip.dataset.cardImage;

    const inWishlist = Boolean(window.Wishlist?.has?.(chip.dataset.variantId));
    wishlistButton.setAttribute('aria-pressed', inWishlist ? 'true' : 'false');
    wishlistButton.classList.toggle('is-active', inWishlist);
  }

  // Compare: sibling chips are a different product, so point the card's
  // compare identity (and its checkbox) at the sibling, then let
  // compare-products.js re-sync the checked state against the stored set.
  const chipProductId = chip.dataset.productId;
  if (chipProductId && card.dataset.compareId !== chipProductId) {
    card.dataset.compareId = chipProductId;
    if (chip.dataset.productTitle) card.dataset.compareTitle = chip.dataset.productTitle;
    if (chip.dataset.cardImage) card.dataset.compareImage = chip.dataset.cardImage;
    if (href) card.dataset.compareUrl = href;

    const compareCheckbox = card.querySelector('input[data-compare-check]');
    if (compareCheckbox) compareCheckbox.dataset.compareCheck = chipProductId;

    document.dispatchEvent(new CustomEvent('compare:grid-updated'));
  }
});
})();
